"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports["default"] = void 0;

var _history = require("history");

var _urlSearchParams = _interopRequireDefault(require("@ungap/url-search-params"));

function _interopRequireDefault(obj) {
  return obj && obj.__esModule ? obj : {
    "default": obj
  };
}

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    if (enumerableOnly) symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    });
    keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i] != null ? arguments[i] : {};

    if (i % 2) {
      ownKeys(source, true).forEach(function (key) {
        _defineProperty(target, key, source[key]);
      });
    } else if (Object.getOwnPropertyDescriptors) {
      Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
      ownKeys(source).forEach(function (key) {
        Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
      });
    }
  }

  return target;
}

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}
/**
 * Sets up bidirectional synchronisation between a Redux store and window
 * location query parameters.
 *
 * @param {Object} options.store - The redux store object (= an object `{dispatch, getState}`).
 * @param {Object} options.params - The query parameters in the location to keep in sync.
 * @param {function value => action} options.params[].action - The action creator to be invoked with
 *     the parameter value. Should return an action that sets this value in the store.
 * @param {function state => value} options.params[].selector - The function that gets the value
 *     given the state.
 * @param {*} [options.params[].defaultValue] - The value corresponding to absence of the parameter.
 *     You may want this to equal the state's default/initial value. Default: `undefined`.
 * @param {function} [options.params[].valueToString] - The inverse of stringToValue. Specifies how
 *     to cast the value to a string, to be used in the URL. Defaults to javascript's automatic
 *     string conversion.
 * @param {function} [options.params[].stringToValue] - The inverse of valueToString. Specifies how
 *     to parse the parameter's string value to your desired value type. Defaults to the identity
 *     function (i.e. you get the string as it is).
 * @param {string} options.initialTruth - If set, indicates whose values to sync to the other,
 *     initially. Can be either `'location'` or `'store'`. If not set, the first of them that
 *     changes will set the other, which is not recommended. Usually you will want to use
 *     `location`.
 * @param {boolean} [options.replaceState] - If truthy, update location using
 *     history.replaceState instead of history.pushState, to not fill the browser history.
 * @param {Object} [options.history] - If you use the 'history' module, e.g. when using a router,
 *     pass your history object here in order to ensure all code uses the same instance.
 */


function ReduxQuerySync(_ref) {
  var store = _ref.store,
      params = _ref.params,
      replaceState = _ref.replaceState,
      initialTruth = _ref.initialTruth,
      _ref$history = _ref.history,
      history = _ref$history === void 0 ? (0, _history.createBrowserHistory)() : _ref$history;
  var dispatch = store.dispatch; // Two bits of state used to not respond to self-induced updates.

  var ignoreLocationUpdate = false;
  var ignoreStateUpdate = false; // Keeps the last seen values for comparing what has changed.

  var lastQueryValues; // Helper function to support history module v3 as well as v4.

  function getLocation(history) {
    if (Object.hasOwnProperty.call(history, 'location')) {
      return history.location;
    } else if (Object.hasOwnProperty.call(history, 'getCurrentLocation')) {
      return history.getCurrentLocation();
    }
  }

  function getQueryValues(location) {
    var locationParams = new _urlSearchParams["default"](location.search);
    var queryValues = {};
    Object.keys(params).forEach(function (param) {
      var _params$param = params[param],
          defaultValue = _params$param.defaultValue,
          _params$param$stringT = _params$param.stringToValue,
          stringToValue = _params$param$stringT === void 0 ? function (s) {
        return s;
      } : _params$param$stringT;
      var valueString = locationParams.get(param);
      var value = valueString === null ? defaultValue : stringToValue(valueString);
      queryValues[param] = value;
    });
    return queryValues;
  }

  function handleLocationUpdate(location) {
    // Support history v5
    if (location.location !== undefined) {
      location = location.location;
    } // Ignore the event if the location update was induced by ourselves.


    if (ignoreLocationUpdate) return;
    var state = store.getState(); // Read the values of the watched parameters

    var queryValues = getQueryValues(location); // For each parameter value that changed, we dispatch the corresponding action.

    var actionsToDispatch = [];
    Object.keys(queryValues).forEach(function (param) {
      var value = queryValues[param]; // Process the parameter both on initialisation and if it has changed since last time.
      // (should we just do this unconditionally?)

      if (lastQueryValues === undefined || lastQueryValues[param] !== value) {
        var _params$param2 = params[param],
            selector = _params$param2.selector,
            action = _params$param2.action; // Dispatch the action to update the state if needed.
        // (except on initialisation, this should always be needed)

        if (selector(state) !== value) {
          actionsToDispatch.push(action(value));
        }
      }
    });
    lastQueryValues = queryValues;
    ignoreStateUpdate = true;
    actionsToDispatch.forEach(function (action) {
      dispatch(action);
    });
    ignoreStateUpdate = false; // Update the location the again: reducers may have e.g. corrected invalid values.

    handleStateUpdate({
      replaceState: true
    });
  }

  function handleStateUpdate(_ref2) {
    var replaceState = _ref2.replaceState;
    if (ignoreStateUpdate) return;
    var state = store.getState();
    var location = getLocation(history); // Parse the current location's query string.

    var locationParams = new _urlSearchParams["default"](location.search); // Replace each configured parameter with its value in the state.

    Object.keys(params).forEach(function (param) {
      var _params$param3 = params[param],
          selector = _params$param3.selector,
          defaultValue = _params$param3.defaultValue,
          _params$param3$valueT = _params$param3.valueToString,
          valueToString = _params$param3$valueT === void 0 ? function (v) {
        return "".concat(v);
      } : _params$param3$valueT;
      var value = selector(state);

      if (value === defaultValue) {
        locationParams["delete"](param);
      } else {
        locationParams.set(param, valueToString(value));
      }

      lastQueryValues[param] = value;
    });
    var newLocationSearchString = "?".concat(locationParams);
    var oldLocationSearchString = location.search || '?'; // Only update location if anything changed.

    if (newLocationSearchString !== oldLocationSearchString) {
      // Update location (but prevent triggering a state update).
      ignoreLocationUpdate = true;
      var newLocation = {
        pathname: location.pathname,
        search: newLocationSearchString,
        hash: location.hash,
        state: location.state
      };
      replaceState ? history.replace(newLocation) : history.push(newLocation);
      ignoreLocationUpdate = false;
    }
  } // Sync location to store on every location change, and vice versa.


  var unsubscribeFromLocation = history.listen(handleLocationUpdate);
  var unsubscribeFromStore = store.subscribe(function () {
    return handleStateUpdate({
      replaceState: replaceState
    });
  }); // Sync location to store now, or vice versa, or neither.

  if (initialTruth === 'location') {
    handleLocationUpdate(getLocation(history));
  } else {
    // Just set the last seen values to later compare what changed.
    lastQueryValues = getQueryValues(getLocation(history));
  }

  if (initialTruth === 'store') {
    handleStateUpdate({
      replaceState: true
    });
  }

  return function unsubscribe() {
    unsubscribeFromLocation();
    unsubscribeFromStore();
  };
}
/**
 * For convenience, one can set up the synchronisation by passing this enhancer to createStore.
 *
 * @example
 *
 *     const storeEnhancer = ReduxQuerySync.enhancer({params, initialTruth: 'location'})
 *     const store = createStore(reducer, initialState, storeEnhancer)
 *
 * Arguments are equal to those of ReduxQuerySync itself, except that `store` can now be omitted.
 */


ReduxQuerySync.enhancer = function makeStoreEnhancer(config) {
  return function (storeCreator) {
    return function (reducer, initialState, enhancer) {
      // Create the store as usual.
      var store = storeCreator(reducer, initialState, enhancer); // Hook up our listeners.

      ReduxQuerySync(_objectSpread({
        store: store
      }, config));
      return store;
    };
  };
};

var _default = ReduxQuerySync;
exports["default"] = _default;